import { useState } from 'react';
import { useFormat } from '@/hooks/useAccess';
import {
  useAccountsQuery,
  useExpenseCategoriesQuery,
  useUpdateExpenseMutation,
  type Expense,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatMoney, isoDate } from '@/lib/format';
import { Alert, Button, Input, Modal, Select, Textarea } from '@/components/ui';
import { ExpenseCategoryPicker } from '@/components/ExpenseCategoryPicker';

const RECURRENCE = [
  ['MONTHLY', 'Every month'],
  ['WEEKLY', 'Every week'],
  ['QUARTERLY', 'Every three months'],
  ['YEARLY', 'Every year'],
  ['DAILY', 'Every day'],
] as const;

/**
 * Correcting an expense.
 *
 * A draft is just a form, and editing it changes nothing but the form. An
 * expense that has already been **paid** is money out of a real account, so
 * changing the figure is a correction the cash book hears about: the original
 * entry is reversed and the new one posted, both dated today. The form says so
 * before it is saved, because a number that quietly moves a drawer is how two
 * sets of books start.
 */
export function ExpenseEditModal({
  expense,
  onClose,
  onSaved,
}: {
  expense: Expense;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const format = useFormat();
  const { data: categories } = useExpenseCategoriesQuery();
  const { data: accounts } = useAccountsQuery({ limit: 50 });
  const [update, state] = useUpdateExpenseMutation();

  const [form, setForm] = useState({
    categoryId: expense.category.id,
    description: expense.description,
    amount: expense.amount,
    taxAmount: expense.taxAmount,
    accountId: expense.accountId ?? '',
    reference: expense.reference ?? '',
    notes: expense.notes ?? '',
    expenseDate: isoDate(new Date(expense.expenseDate)),
    isRecurring: expense.isRecurring ?? false,
    recurrenceRule: expense.recurrenceRule ?? 'MONTHLY',
  });
  const [failure, setFailure] = useState<string | null>(null);

  const fieldErrors = apiFieldErrors(state.error);
  const paid = expense.status === 'PAID';
  const total = Number(form.amount || 0) + Number(form.taxAmount || 0);
  const moneyMoves =
    paid && (total !== Number(expense.total) || form.accountId !== (expense.accountId ?? ''));

  const save = async (): Promise<void> => {
    setFailure(null);
    try {
      await update({
        id: expense.id,
        body: {
          categoryId: form.categoryId,
          description: form.description,
          amount: Number(form.amount),
          taxAmount: Number(form.taxAmount || 0),
          ...(form.accountId ? { accountId: form.accountId } : {}),
          reference: form.reference,
          notes: form.notes,
          expenseDate: form.expenseDate,
          isRecurring: form.isRecurring,
          ...(form.isRecurring ? { recurrenceRule: form.recurrenceRule } : {}),
        },
      }).unwrap();

      onSaved?.();
      onClose();
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That expense could not be changed'));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Edit ${expense.number}`}
      description={`${expense.status.toLowerCase().replace('_', ' ')} · currently ${formatMoney(
        expense.total,
        format,
      )}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={state.isLoading}>Cancel</Button>
          <Button
            loading={state.isLoading}
            disabled={!form.description || !(Number(form.amount) >= 0)}
            onClick={() => void save()}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {failure && <Alert tone="danger">{failure}</Alert>}

        {moneyMoves && (
          <Alert tone="warning">
            This expense has been paid. Saving takes the old{' '}
            {formatMoney(expense.total, format)} back into
            {expense.account ? ` ${expense.account.name}` : ' the account it came from'} and pays{' '}
            {formatMoney(String(total), format)} out again, both dated today.
          </Alert>
        )}

        <ExpenseCategoryPicker
          required
          value={form.categoryId}
          onChange={(categoryId) => setForm({ ...form, categoryId })}
          categories={categories ?? []}
          error={fieldErrors.categoryId}
        />

        <Input
          label="Description"
          required
          value={form.description}
          error={fieldErrors.description}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Input
            label="Amount"
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            error={fieldErrors.amount}
            onChange={(event) => setForm({ ...form, amount: event.target.value })}
          />
          <Input
            label="Tax"
            type="number"
            min="0"
            step="0.01"
            value={form.taxAmount}
            onChange={(event) => setForm({ ...form, taxAmount: event.target.value })}
          />
          <Input
            label="Date"
            type="date"
            value={form.expenseDate}
            onChange={(event) => setForm({ ...form, expenseDate: event.target.value })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={paid ? 'Paid from' : 'To be paid from'}
            value={form.accountId}
            onChange={(event) => setForm({ ...form, accountId: event.target.value })}
            hint={paid ? 'Changing this moves the money between accounts.' : 'Optional until it is paid.'}
          >
            <option value="">{paid ? 'Unchanged' : 'Decide later'}</option>
            {accounts?.items
              .filter((account) => account.isActive)
              .map((account) => (
                <option key={account.id} value={account.id}>{account.name}</option>
              ))}
          </Select>
          <Input
            label="Reference"
            value={form.reference}
            onChange={(event) => setForm({ ...form, reference: event.target.value })}
          />
        </div>

        <Textarea
          label="Notes"
          rows={2}
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
        />

        <div className="rounded-lg border border-subtle p-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-4 rounded border-subtle text-brand-600"
              checked={form.isRecurring}
              onChange={(event) => setForm({ ...form, isRecurring: event.target.checked })}
            />
            <span>
              This expense comes round again
              <span className="block text-xs text-muted">
                Each repeat is raised as a draft for someone to check — nothing is paid
                automatically.
              </span>
            </span>
          </label>
          {form.isRecurring && (
            <div className="mt-3">
              <Select
                label="How often"
                value={form.recurrenceRule}
                onChange={(event) => setForm({ ...form, recurrenceRule: event.target.value })}
              >
                {RECURRENCE.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
